import { Fragment, Suspense } from 'react';

import RichTextImage from 'molecules/Image/RichTextImage';
import PodcastPlayer from 'molecules/Podcast';
import Quiz from 'molecules/Quiz';
import RichText from 'molecules/RichText';
import TableBuilderComponent from 'molecules/Table';
import TheDownloader from 'molecules/TheDownloader';
import Video from 'molecules/Video';
import FooterNotes from 'molecules/footerNote';

import BuilderPixel from 'components/Builder/BuilderPixel';
import ContentEditor from 'components/ContentEditor';
import ConversionPanel from 'components/ConversionPanel';
import FAQ from 'components/FAQ';
import FAQPage from 'components/FAQPage';
import AgentQuote from 'components/Forms/AgentQuote';
import ContactForm from 'components/Forms/ContactForm';
import DataPrivacy from 'components/Forms/DataPrivacy';
import TechSupport from 'components/Forms/TechSupport';
import GridLeadership from 'components/GridLeadership';
import LightboxHero from 'components/Heros/LightboxHero';
import PageHeaderHero from 'components/Heros/PageHeaderHero';
import QuoteHero from 'components/Heros/QuoteHero';
import SideNavHero from 'components/Heros/SideNavHero';
import StandardHero from 'components/Heros/StandardHero';
import IconCards from 'components/IconCards';
import ImageCards from 'components/ImageCards';
import ImageMarque from 'components/ImageMarque';
import LeaderShipDeck from 'components/LeaderShipDeck';
import Leadership from 'components/Leadership';
import MediaCoverage from 'components/MediaCoverage';
import Metrics from 'components/Metrics';
import ProductListing from 'components/ProductListing';
import ResourceCarousel from 'components/ResourceCarousel';
import SplitContent from 'components/SplitContent';
import BasicSwitchback from 'components/Switchbacks/BasicSwitchback';
import CalloutSwitchback from 'components/Switchbacks/CalloutSwitchback';
import TextWithBackgroundImageSwitchback from 'components/Switchbacks/TextWithBackgroundImage';
import TextWithForegroundImageSwitchback from 'components/Switchbacks/TextWithForegroundImage';
import TableMarque from 'components/TableMarque';
import Testimonial from 'components/Testimonial';
import TextCards from 'components/TextCards';
import TextSection from 'components/TextSection';
import ComponentVideo from 'components/Video';
import TwoColumnCard from 'components/twoColumnCards';

import { isNonEmptyArray } from 'utils/functions';

import type { BuilderElement } from '@builder.io/sdk';
import type { FC, Key } from 'react';

export interface ComponentGeneratorProps {
  components?: BuilderElement[];
}

const componentInspector = (component: BuilderElement, key: Key) => {
  const { name, options } = component?.component || {};
  const componentProps = { key, ...options };
  if (component?.id?.includes('builder-pixel')) {
    return <BuilderPixel {...options} />;
  }
  switch (name) {
    case 'Agent Quote':
      return <AgentQuote {...componentProps} key={key} />;
    case 'Basic Switchback':
      return <BasicSwitchback {...componentProps} key={key} />;
    case 'Callout Switchback':
      return <CalloutSwitchback {...componentProps} key={key} />;
    case 'Component Video':
      return <ComponentVideo {...componentProps} key={key} />;
    case 'Contact Form':
      return <ContactForm {...componentProps} key={key} />;
    case 'Data Privacy Form':
      return <DataPrivacy {...componentProps} key={key} />;
    case 'Content Editor':
      return <ContentEditor {...componentProps} key={key} />;
    case 'Conversion Panel':
      return <ConversionPanel {...componentProps} key={key} />;
    case 'Grid Leadership':
      return <GridLeadership {...componentProps} key={key} />;
    case 'Faq':
      return <FAQ {...componentProps} key={key} />;
    case 'Faq Page':
      return (
        <Suspense>
          <FAQPage {...componentProps} key={key} />
        </Suspense>
      );
    case 'Footer Notes':
      return <FooterNotes {...componentProps} key={key} />;
    case 'Header Hero':
      return <PageHeaderHero {...componentProps} key={key} />;
    case 'HCI Image':
      return <RichTextImage {...componentProps} key={key} />;
    case 'HCI Download Component':
      return <TheDownloader {...componentProps} key={key} />;
    case 'HCI Podcast':
      return <PodcastPlayer {...componentProps} key={key} />;
    case 'HCI Table Component':
      return <TableBuilderComponent {...componentProps} key={key} />;
    case 'HCI Text Content':
      return <RichText {...componentProps} key={key} />;
    case 'HCI Wistia Video':
      return <Video {...componentProps} key={key} />;
    case 'Icon Cards':
      return <IconCards {...componentProps} key={key} />;
    case 'Image Cards':
      return <ImageCards {...componentProps} key={key} />;
    case 'Image Marque':
      return <ImageMarque {...componentProps} key={key} />;
    case 'Leadership':
      return <Leadership {...componentProps} key={key} />;
    case 'Leadership Deck':
      return <LeaderShipDeck {...componentProps} key={key} />;
    case 'Lightbox Hero':
      return <LightboxHero {...componentProps} key={key} />;
    case 'Media Coverage':
      return <MediaCoverage {...componentProps} key={key} />;
    case 'Metrics':
      return <Metrics {...componentProps} key={key} />;
    case 'Quote Hero':
      return <QuoteHero {...componentProps} key={key} />;
    case 'Resource Carousel':
      return <ResourceCarousel {...componentProps} key={key} />;
    case 'SideNav Hero':
      return (
        <Suspense>
          <SideNavHero {...componentProps} key={key} />
        </Suspense>
      );
    case 'Split Content':
      return <SplitContent {...componentProps} key={key} />;
    case 'Standard Hero':
      return <StandardHero {...componentProps} key={key} />;
    case 'Product Listing':
      return <ProductListing {...componentProps} key={key} />;
    case 'Table Marque':
      return <TableMarque {...componentProps} key={key} />;
    case 'Tech Support':
      return <TechSupport {...componentProps} key={key} />;
    case 'Testimonial':
      return <Testimonial {...componentProps} key={key} />;
    case 'Text Cards':
      return <TextCards {...componentProps} key={key} />;
    case 'Text Section':
      return <TextSection {...componentProps} key={key} />;
    case 'Text With Background Image Switchback':
      return <TextWithBackgroundImageSwitchback {...componentProps} key={key} />;
    case 'Text With Foreground Image Switchback':
      return <TextWithForegroundImageSwitchback {...componentProps} key={key} />;
    case 'Two Column Card':
      return <TwoColumnCard {...componentProps} key={key} />;
    case 'Quiz':
      return <Quiz {...componentProps} key={key} />;
    default:
      console.log(`Component not found: ${name}`);

      return null;
  }
};

const ComponentRenderer: FC<{ components: BuilderElement[] }> = ({ components }) => (
  <>
    {components.map((component, index) => (
      <Fragment key={component.id || index}>{componentInspector(component, component.id || index)}</Fragment>
    ))}
  </>
);

export const ComponentGenerator: FC<ComponentGeneratorProps> = ({ components = [] }) =>
  isNonEmptyArray(components) ? <ComponentRenderer components={components} /> : null;

export default ComponentGenerator;
